import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { buttonVariants } from '@/components/ui/button';
import { NavItem } from '@/components/navigation/NavItem';
import type { AppIcon } from '@/lib/icons';
import { Icons } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { teamApi } from '@/lib/api/team/teamApi';
import type { Team } from 'dico-types';

export type SettingsTabId =
  | 'profile'
  | 'workspace'
  | 'projects'
  | 'members'
  | 'integrations'
  | 'tags'
  | 'statuses'
  | 'priorities'
  | 'insights'
  | 'billing'
  | 'usage'
  | 'events'
  | 'models'
  | 'pull-requests'
  | 'instructions'
  | 'sandbox'
  | 'api-keys'
  | 'preferences'
  | 'team';

interface SettingsTab {
  id: SettingsTabId;
  label: string;
  icon: AppIcon;
}

interface SettingsGroup {
  id: string;
  label: string;
  tabs: SettingsTab[];
}

const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    id: 'account',
    label: 'Account',
    tabs: [{ id: 'profile', label: 'Profile', icon: Icons.CircleUserRound }]
  },
  {
    id: 'administration',
    label: 'Administration',
    tabs: [
      { id: 'workspace', label: 'Workspace', icon: Icons.Building2 },
      { id: 'projects', label: 'Projects', icon: Icons.FolderOpen },
      { id: 'members', label: 'Members', icon: Icons.UserGroup },
      { id: 'integrations', label: 'Integrations', icon: Icons.Plug },
      { id: 'tags', label: 'Tags', icon: Icons.Tag },
      { id: 'statuses', label: 'Statuses', icon: Icons.Layers },
      { id: 'priorities', label: 'Priorities', icon: Icons.Bolt },
      { id: 'insights', label: 'Insights', icon: Icons.ChartNoAxesColumnIncreasing },
      // { id: 'billing', label: 'Billing', icon: Icons.Billing },
      // { id: 'usage', label: 'Usage', icon: Icons.ChartNoAxesColumn },
      { id: 'events', label: 'Events', icon: Icons.History }
    ]
  },
  // {
  //   id: 'agent',
  //   label: 'Agent',
  //   tabs: [
  //     { id: 'models', label: 'Cpu', icon: Icons.Brain },
  //     { id: 'instructions', label: 'Instructions', icon: Icons.ScrollText },
  //     { id: 'sandbox', label: 'Sandbox', icon: Icons.CodeBracket }
  //   ]
  // },
  {
    id: 'developer',
    label: 'Developer',
    tabs: [{ id: 'api-keys', label: 'API Keys', icon: Icons.Lock }]
  }
];

export function getSettingsTabLabel(tabId: string): string {
  for (const group of SETTINGS_GROUPS) {
    const tab = group.tabs.find((t) => t.id === tabId);
    if (tab) return tab.label;
  }
  return 'Settings';
}

export function getSettingsTabLabelForTabId(tabId: string): string {
  return getSettingsTabLabel(tabId);
}

interface SettingsSidebarProps {
  activeTabId: string;
  className?: string;
  showBackToWorkspace?: boolean;
  activeTeamId?: string;
}

export function SettingsSidebar({
  activeTabId,
  className,
  showBackToWorkspace = true,
  activeTeamId
}: SettingsSidebarProps) {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceId = params.id;
  const basePath = `/workspace/${workspaceId}/settings`;
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    const loadTeams = async () => {
      if (!workspaceId) return;
      try {
        const result = await teamApi.getTeams(workspaceId);
        if (result.success && result.data) {
          setTeams(result.data);
        }
      } catch (error) {
        console.error('Failed to load teams:', error);
      }
    };

    loadTeams();
  }, [workspaceId]);

  return (
    <div className={cn('h-full overflow-y-auto', 'w-56 pt-3', className)}>
      {showBackToWorkspace && (
        <div className="flex items-center gap-2 px-2 pb-2">
          <Link
            to={`/workspace/${workspaceId}`}
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'sm' }),
              'flex items-center gap-1.5 px-2'
            )}
          >
            <MonoIcon type={Icons.ChevronLeft} className="text-muted-foreground" />
            <span className="text-muted-foreground text-sm">Back</span>
          </Link>
        </div>
      )}

      <nav className="space-y-4 px-2">
        {SETTINGS_GROUPS.map((group) => (
          <div key={group.id} className="space-y-0.5">
            <p className="text-muted-foreground px-2 pb-1 text-xs font-medium tracking-wide">
              {group.label}
            </p>
            {group.tabs.map((tab) => {
              const href = `${basePath}/${tab.id}`;
              const isActive = activeTabId === tab.id;
              return (
                <NavItem
                  key={tab.id}
                  to={href}
                  label={tab.label}
                  icon={tab.icon}
                  active={isActive}
                />
              );
            })}
          </div>
        ))}

        {/* Teams Section */}
        {teams.length > 0 && (
          <div className="space-y-0.5">
            <p className="text-muted-foreground px-2 pb-1 text-xs font-medium tracking-wide">
              Teams
            </p>
            {teams.map((team) => {
              const href = `${basePath}/team/${team.id}`;
              const isActive = activeTeamId === team.id;
              return (
                <NavItem
                  key={team.id}
                  to={href}
                  label={team.name}
                  icon={Icons.Users}
                  active={isActive}
                />
              );
            })}
          </div>
        )}
      </nav>
    </div>
  );
}
